import React, { useState, useEffect, useCallback } from 'react';
import { notificationsService } from '../services';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Mail, Loader2, RefreshCw, Clock, Send, CheckCircle, XCircle, 
  AlertCircle, ChevronLeft, ChevronRight, Trash2, Eye, Calendar,
  ChevronDown, ChevronUp
} from 'lucide-react';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { getApiError } from '@/lib/utils';

const statusColors = {
  completed: 'bg-green-100 text-green-800',
  pending: 'bg-amber-100 text-amber-800',
  processing: 'bg-blue-100 text-blue-800',
  failed: 'bg-red-100 text-red-800'
};

const modeLabels = {
  immediate: 'Immediate',
  batch_5pm: '5 PM Batch'
};

const typeLabels = {
  publish: 'Schedule Publish',
  update: 'Schedule Update',
  cancellation: 'Cancellation',
  schedule_change: 'Schedule Change',
  batch_return: 'Batch Returned'
};

const typeColors = {
  publish: 'bg-blue-100 text-blue-800',
  update: 'bg-indigo-100 text-indigo-800',
  cancellation: 'bg-red-100 text-red-800',
  schedule_change: 'bg-amber-100 text-amber-800',
  batch_return: 'bg-orange-100 text-orange-800'
};

export default function NotificationLogsPage() {
  const { canManageJobs, isAuditor } = useAuth();
  const { toast } = useToast();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [emailStatus, setEmailStatus] = useState(null);
  const limit = 20;
  
  // Filter states - default to current month
  const [filterStartDate, setFilterStartDate] = useState(() => 
    format(startOfMonth(new Date()), 'yyyy-MM-dd')
  );
  const [filterEndDate, setFilterEndDate] = useState(() => 
    format(endOfMonth(new Date()), 'yyyy-MM-dd')
  );
  
  // Detail view states
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [batchDetails, setBatchDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  
  // Expanded rows for inline details
  const [expandedRows, setExpandedRows] = useState(new Set());

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await notificationsService.getLogs({
        limit,
        offset: page * limit,
        start_date: filterStartDate || undefined,
        end_date: filterEndDate || undefined
      });
      setLogs(response.logs || []);
      setTotal(response.total || 0);
    } catch (err) {
      console.error('Failed to load notification logs:', err);
      toast({
        title: 'Error',
        description: 'Failed to load notification history',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [page, filterStartDate, filterEndDate, toast]);

  const loadEmailStatus = useCallback(async () => {
    try {
      const status = await notificationsService.getStatus();
      setEmailStatus(status);
    } catch (err) {
      console.error('Failed to check email status:', err);
    }
  }, []);

  useEffect(() => {
    loadLogs();
    loadEmailStatus();
  }, [loadLogs, loadEmailStatus]);

  const totalPages = Math.ceil(total / limit);

  // Toggle row expansion
  const toggleRowExpansion = async (batchId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(batchId)) {
      newExpanded.delete(batchId);
    } else {
      newExpanded.add(batchId);
      // Load batch details if not already loaded
      if (!batchDetails || batchDetails.batch_id !== batchId) {
        await loadBatchDetails(batchId);
      }
    }
    setExpandedRows(newExpanded);
  };
  
  // Load batch details
  const loadBatchDetails = async (batchId) => {
    setLoadingDetails(true);
    try {
      const details = await notificationsService.getBatchDetails(batchId);
      setBatchDetails(details);
    } catch (err) {
      console.error('Failed to load batch details:', err);
      toast({
        title: 'Error',
        description: 'Failed to load notification details',
        variant: 'destructive'
      });
    } finally {
      setLoadingDetails(false);
    }
  };
  
  // Open detail dialog
  const openDetailDialog = async (batch) => {
    setSelectedBatch(batch);
    setDetailDialogOpen(true);
    await loadBatchDetails(batch.id);
  };

  // Delete a notification batch
  const handleDelete = async (batchId) => {
    if (!window.confirm('Are you sure you want to delete this notification batch? This cannot be undone.')) {
      return;
    }
    
    try {
      await notificationsService.deleteBatch(batchId);
      toast({
        title: 'Deleted',
        description: 'Notification batch deleted successfully',
      });
      loadLogs();
    } catch (err) {
      console.error('Failed to delete batch:', err);
      toast({
        title: 'Error',
        description: getApiError(err, 'Failed to delete notification batch'),
        variant: 'destructive'
      });
    }
  };

  // Reset filters to current month
  const resetFilters = () => {
    setFilterStartDate(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
    setFilterEndDate(format(endOfMonth(new Date()), 'yyyy-MM-dd'));
    setPage(0);
  };

  if (!canManageJobs) {
    return (
      <div className="container mx-auto py-8">
        <Card>
          <CardContent className="pt-6">
            <p className="text-muted-foreground">You don't have permission to view notification logs.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="notification-logs-page">
      <Toaster />
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Mail className="h-6 w-6" />
            Notification History
          </h1>
          <p className="text-muted-foreground">
            View sent schedule notifications and delivery status
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Email Status Indicator */}
          {emailStatus && (
            <div 
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                emailStatus.email_configured 
                  ? "bg-green-100 text-green-800 border border-green-200" 
                  : "bg-amber-100 text-amber-800 border border-amber-200"
              }`}
            >
              <Mail className="h-3 w-3" />
              {emailStatus.email_configured ? 'Email: Configured' : 'Email: Not Configured'}
              {emailStatus.email_configured ? (
                <CheckCircle className="h-3 w-3" />
              ) : (
                <AlertCircle className="h-3 w-3" />
              )}
            </div>
          )}
          
          <Button variant="outline" onClick={loadLogs} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Email Not Configured Warning */}
      {emailStatus && !emailStatus.email_configured && (
        <Card className="mb-6 border-amber-200 bg-amber-50">
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">Email Notifications Not Configured</p>
                <p className="text-sm text-amber-700 mt-1">
                  SendGrid API key is not configured. Email notifications are currently disabled.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-slate-500" />
              <span className="text-sm font-medium text-slate-700">Filter by Date:</span>
            </div>
            <div className="flex-1 flex flex-wrap items-end gap-4">
              <div className="w-40">
                <Label htmlFor="filter-start" className="text-xs text-slate-500">Start Date</Label>
                <Input
                  id="filter-start"
                  type="date"
                  value={filterStartDate}
                  onChange={(e) => {
                    setFilterStartDate(e.target.value);
                    setPage(0);
                  }}
                  className="h-9"
                />
              </div>
              <div className="w-40">
                <Label htmlFor="filter-end" className="text-xs text-slate-500">End Date</Label>
                <Input
                  id="filter-end"
                  type="date"
                  value={filterEndDate}
                  onChange={(e) => {
                    setFilterEndDate(e.target.value);
                    setPage(0);
                  }}
                  className="h-9"
                />
              </div>
              <Button variant="ghost" size="sm" onClick={resetFilters}>
                Reset to Current Month
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Notification Batches</CardTitle>
          <CardDescription>
            {total} notification{total !== 1 ? 's' : ''} found
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Mail className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No notification logs found for this date range</p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8"></TableHead>
                    <TableHead>Date/Time</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Mode</TableHead>
                    <TableHead>Schedule Range</TableHead>
                    <TableHead className="text-center">Recipients</TableHead>
                    <TableHead className="text-center">Jobs</TableHead>
                    <TableHead className="text-center">Sent</TableHead>
                    <TableHead className="text-center">Failed</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <React.Fragment key={log.id}>
                      <TableRow 
                        className="cursor-pointer hover:bg-slate-50"
                        onClick={() => toggleRowExpansion(log.id)}
                      >
                        <TableCell>
                          {expandedRows.has(log.id) ? (
                            <ChevronUp className="h-4 w-4 text-slate-400" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-slate-400" />
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">
                            {format(new Date(log.created_at), 'd MMM yyyy')}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {format(new Date(log.created_at), 'h:mm a')}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={typeColors[log.notification_type] || 'bg-gray-100 text-gray-800'}>
                            {typeLabels[log.notification_type] || log.notification_type || 'Publish'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            {log.send_mode === 'immediate' ? (
                              <Send className="h-3.5 w-3.5 text-blue-500" />
                            ) : (
                              <Clock className="h-3.5 w-3.5 text-amber-500" />
                            )}
                            <span className="text-sm">{modeLabels[log.send_mode] || log.send_mode}</span>
                          </div>
                          {log.scheduled_send_at && log.status === 'pending' && (
                            <div className="text-xs text-muted-foreground mt-0.5">
                              Scheduled: {format(new Date(log.scheduled_send_at), 'h:mm a')}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {log.start_date && format(new Date(log.start_date), 'd MMM')}
                            {log.end_date && ` → ${format(new Date(log.end_date), 'd MMM yyyy')}`}
                          </div>
                        </TableCell>
                        <TableCell className="text-center font-medium">{log.total_auditors}</TableCell>
                        <TableCell className="text-center font-medium">{log.total_jobs}</TableCell>
                        <TableCell className="text-center">
                          <span className={log.recipients_sent > 0 ? 'text-green-600 font-medium' : ''}>
                            {log.recipients_sent}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className={log.recipients_failed > 0 ? 'text-red-600 font-medium' : ''}>
                            {log.recipients_failed}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={statusColors[log.status]}>
                            {log.status === 'completed' && <CheckCircle className="h-3 w-3 mr-1" />}
                            {log.status === 'failed' && <XCircle className="h-3 w-3 mr-1" />}
                            {log.status === 'pending' && <Clock className="h-3 w-3 mr-1" />}
                            {log.status === 'processing' && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                            {log.status.charAt(0).toUpperCase() + log.status.slice(1)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => {
                                e.stopPropagation();
                                openDetailDialog(log);
                              }}
                              title="View Details"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(log.id);
                              }}
                              title="Delete"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                      
                      {/* Expanded Details Row */}
                      {expandedRows.has(log.id) && (
                        <TableRow>
                          <TableCell colSpan={10} className="bg-slate-50 p-4">
                            {loadingDetails && batchDetails?.batch_id !== log.id ? (
                              <div className="flex items-center justify-center py-4">
                                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                                Loading details...
                              </div>
                            ) : batchDetails && batchDetails.batch_id === log.id ? (
                              <div className="space-y-3">
                                <h4 className="font-medium text-sm">Recipients ({batchDetails.recipients?.length || 0})</h4>
                                {batchDetails.recipients?.length > 0 ? (
                                  <Table>
                                    <TableHeader>
                                      <TableRow>
                                        <TableHead>Auditor</TableHead>
                                        <TableHead>Email</TableHead>
                                        <TableHead className="text-center">Jobs</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead>Sent At</TableHead>
                                        <TableHead>Error</TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {batchDetails.recipients.map((r, idx) => (
                                        <TableRow key={idx}>
                                          <TableCell className="font-medium">{r.auditor_name}</TableCell>
                                          <TableCell className="text-sm text-slate-600">{r.email}</TableCell>
                                          <TableCell className="text-center">{r.job_count}</TableCell>
                                          <TableCell>
                                            {r.sent_at ? (
                                              <Badge variant="outline" className="bg-green-100 text-green-800">
                                                <CheckCircle className="h-3 w-3 mr-1" />
                                                Sent
                                              </Badge>
                                            ) : r.error_message ? (
                                              <Badge variant="outline" className="bg-red-100 text-red-800">
                                                <XCircle className="h-3 w-3 mr-1" />
                                                Failed
                                              </Badge>
                                            ) : (
                                              <Badge variant="outline" className="bg-amber-100 text-amber-800">
                                                <Clock className="h-3 w-3 mr-1" />
                                                Pending
                                              </Badge>
                                            )}
                                          </TableCell>
                                          <TableCell className="text-sm text-slate-600">
                                            {r.sent_at ? format(new Date(r.sent_at), 'd MMM h:mm a') : '-'}
                                          </TableCell>
                                          <TableCell className="text-sm text-red-600 max-w-xs truncate">
                                            {r.error_message || '-'}
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                ) : (
                                  <p className="text-sm text-muted-foreground">No recipient details available</p>
                                )}
                              </div>
                            ) : (
                              <div className="text-center py-4 text-muted-foreground">
                                <Button variant="outline" size="sm" onClick={() => loadBatchDetails(log.id)}>
                                  Load Details
                                </Button>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <div className="text-sm text-muted-foreground">
                    Page {page + 1} of {totalPages}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(0, p - 1))}
                      disabled={page === 0}
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                      disabled={page >= totalPages - 1}
                    >
                      Next
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent
          className="dialog-resizable"
          style={{ width: '56rem', maxHeight: '80vh' }}
          data-testid="notification-detail-dialog"
        >
          <DialogHeader>
            <DialogTitle>Notification Batch Details</DialogTitle>
            <DialogDescription>
              {selectedBatch && (
                <>
                  Created on {format(new Date(selectedBatch.created_at), 'MMMM d, yyyy h:mm a')} • 
                  {modeLabels[selectedBatch.send_mode] || selectedBatch.send_mode} mode
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {loadingDetails ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : batchDetails ? (
            <div className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-4 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg text-center">
                  <p className="text-2xl font-bold">{batchDetails.total_auditors}</p>
                  <p className="text-sm text-slate-600">Auditors</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg text-center">
                  <p className="text-2xl font-bold">{batchDetails.total_jobs}</p>
                  <p className="text-sm text-slate-600">Jobs</p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-600">{batchDetails.recipients_sent}</p>
                  <p className="text-sm text-green-700">Sent</p>
                </div>
                <div className="p-4 bg-red-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-red-600">{batchDetails.recipients_failed}</p>
                  <p className="text-sm text-red-700">Failed</p>
                </div>
              </div>
              
              {/* Recipients Table */}
              <div>
                <h3 className="font-semibold mb-3">Recipients</h3>
                {batchDetails.recipients?.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Auditor</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead className="text-center">Jobs</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Sent At</TableHead>
                        <TableHead>Error</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {batchDetails.recipients.map((r, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-medium">{r.auditor_name}</TableCell>
                          <TableCell className="text-slate-600">{r.email}</TableCell>
                          <TableCell className="text-center">{r.job_count}</TableCell>
                          <TableCell>
                            {r.sent_at ? (
                              <Badge variant="outline" className="bg-green-100 text-green-800">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Sent
                              </Badge>
                            ) : r.error_message ? (
                              <Badge variant="outline" className="bg-red-100 text-red-800">
                                <XCircle className="h-3 w-3 mr-1" />
                                Failed
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="bg-amber-100 text-amber-800">
                                <Clock className="h-3 w-3 mr-1" />
                                Pending
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {r.sent_at ? format(new Date(r.sent_at), 'd MMM h:mm a') : '-'}
                          </TableCell>
                          <TableCell className="text-red-600 max-w-xs">
                            {r.error_message || '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-muted-foreground text-center py-4">
                    No recipient details available for this batch
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No details available
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
